import React, { useState } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { GlassView } from 'expo-glass-effect';
import Button3D from './Button3D';

interface RouteDeviationModalProps {
  visible: boolean;
  role: 'PASSENGER' | 'DRIVER' | 'RENT_CAR_OWNER' | null;
  /**
   * El motivo que escribió el chofer. ⚠️ ESTE PROP NO EXISTÍA.
   *
   * `map.tsx` ya lo guardaba —`setDeviationModal({ visible: true, role:
   * 'PASSENGER', reason: dev.reason })`— pero el modal no lo recibía ni lo
   * pintaba. Así que al pasajero se le abría una alerta de seguridad a mitad
   * del viaje preguntándole si aprueba un cambio de ruta SIN DECIRLE CUÁL NI
   * POR QUÉ. El motivo viajaba desde el teléfono del chofer, pasaba por
   * Firestore, llegaba al teléfono del pasajero… y se quedaba en una variable.
   *
   * La única forma de enterarse era el push («Motivo: …», dispatch.ts:667), y
   * eso solo si tenía la app cerrada y los avisos encendidos. Quien estaba
   * mirando la pantalla —que es justo quien va en el carro— no lo veía.
   */
  reason?: string | null;
  onApprove: (reason: string) => void;
  onReject: () => void;
}

export default function RouteDeviationModal({
  visible,
  role,
  reason: motivoDelChofer,
  onApprove,
  onReject,
}: RouteDeviationModalProps) {
  const [reason, setReason] = useState<string | null>(null);

  const reasons = [
    'Mucho tráfico / Tapón',
    'Calle cerrada / Reparación',
    'Pasajero lo solicitó',
    'Ruta alternativa más rápida',
    'Otro',
  ];

  const esChofer = role === 'DRIVER';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      // H37 (auditoría 16-sep-2026) — ATRÁS, DECIDIDO A PROPÓSITO.
      // Antes no había `onRequestClose` y Android se tragaba el botón sin que
      // nadie lo hubiera decidido.
      //  · CHOFER (propone el desvío): Atrás = "No". Para él `onReject` solo
      //    cierra su propia propuesta sin escribir nada (ver
      //    `handleDeviationReject` en map.tsx), así que es seguro.
      //  · PASAJERO (aprueba o rechaza): Atrás NO cierra, y es a propósito. El
      //    desvío es una alerta de seguridad que pide una respuesta explícita;
      //    cerrarla sin contestar dejaría la propuesta PENDIENTE y la ventana
      //    volvería a abrirse sola en el siguiente cambio del viaje. Tiene sus
      //    dos botones para salir.
      onRequestClose={() => {
        if (esChofer) onReject();
      }}
    >
      <GlassView style={styles.container} colorScheme="dark">
        <View style={styles.modalContent}>
          <View style={styles.headerIcon}>
            <Ionicons name="warning" size={40} color={Colors.white} />
          </View>

          {/* ⚠️ ESTA PANTALLA LE DECÍA LO MISMO A LOS DOS, Y A UNO DE ELLOS
              LE MENTÍA.

              Decía «Hemos detectado un cambio significativo en la ruta
              original» — y NADIE DETECTA NADA. No se guarda ninguna ruta en el
              viaje, y la navegación la hace Waze o Google Maps FUERA de la
              app: Go Safely no puede saber por dónde va el chofer salvo por su
              punto en el mapa. El desvío lo reporta el chofer A MANO, tocando
              un botón. Decir «hemos detectado» convierte una declaración
              voluntaria en una vigilancia que no existe, y al pasajero le hace
              creer que la app lo está cuidando más de lo que puede.

              Además las dos personas están en situaciones opuestas: el chofer
              PROPONE y el pasajero DECIDE. Tenían el mismo título, el mismo
              texto y los mismos dos botones («¿Estás de acuerdo con este
              cambio de ruta?» también para el que lo está proponiendo). */}
          <Text style={styles.title}>
            {esChofer ? 'Avisar de un cambio de ruta' : 'Tu chofer quiere cambiar la ruta'}
          </Text>
          <Text style={styles.subtitle}>
            {esChofer
              ? 'Le avisamos al pasajero y le pedimos que lo confirme. Mientras no conteste, mantén la ruta.'
              : 'Por tu seguridad, el chofer no puede cambiar la ruta sin avisarte. Tú decides.'}
          </Text>

          {/* EL MOTIVO, QUE ES LO ÚNICO QUE EL PASAJERO NECESITA PARA DECIDIR. */}
          {!esChofer && !!motivoDelChofer && (
            <View style={styles.motivoCaja}>
              <Text style={styles.motivoEtiqueta}>MOTIVO QUE DIO TU CHOFER</Text>
              <Text style={styles.motivoTexto}>{motivoDelChofer}</Text>
            </View>
          )}

          <Text style={styles.questionText}>
            {esChofer ? '¿Por qué necesitas desviarte?' : '¿Estás de acuerdo con este cambio de ruta?'}
          </Text>

          {esChofer && (
            <View style={styles.reasonsContainer}>
              {reasons.map((r) => (
                <TouchableOpacity
                  key={r}
                  style={[styles.reasonChip, reason === r && styles.reasonChipSelected]}
                  onPress={() => setReason(r)}
                >
                  <Text style={[styles.reasonText, reason === r && styles.reasonTextSelected]}>{r}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          <View style={styles.actionButtons}>
            <Button3D
              title={esChofer ? 'Mejor no, cancelar' : 'No, sigue por la misma ruta'}
              color={Colors.error}
              icon={<Ionicons name="close-circle" size={20} color="#FFF" />}
              onPress={onReject}
              style={styles.button}
            />
            <Button3D
              title={esChofer ? 'Avisar al pasajero' : 'Sí, estoy de acuerdo'}
              color={Colors.success}
              icon={<Ionicons name="checkmark-circle" size={20} color="#FFF" />}
              onPress={() => onApprove(reason || 'Sin especificar')}
              style={styles.button}
              // ⚠️ ESTO ESTABA COMENTADO Y DEJABA MANDAR «Sin especificar».
              // El motivo es lo ÚNICO con lo que el pasajero decide: sin él,
              // la pregunta que le llega es «tu chofer se quiere desviar,
              // ¿apruebas?» a secas, y cualquiera diría que no. Ahora el
              // chofer tiene que elegir uno — son cinco toques, no un
              // formulario.
              disabled={esChofer && !reason}
            />
          </View>
          {esChofer && !reason && (
            <Text style={styles.pista}>Elige un motivo para poder avisarle.</Text>
          )}
        </View>
      </GlassView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  modalContent: {
    backgroundColor: Colors.card,
    borderRadius: 24,
    padding: 24,
    width: '100%',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 5,
  },
  headerIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#FF9800',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -60,
    borderWidth: 4,
    borderColor: Colors.card,
    marginBottom: 20,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  motivoCaja: {
    width: '100%',
    backgroundColor: 'rgba(255, 152, 0, 0.10)',
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: 'rgba(255, 152, 0, 0.35)',
  },
  motivoEtiqueta: {
    fontSize: 11,
    fontWeight: '700',
    color: '#B26A00',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  motivoTexto: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text,
  },
  questionText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Colors.text,
    textAlign: 'center',
    marginBottom: 20,
  },
  reasonsContainer: {
    width: '100%',
    marginBottom: 20,
  },
  reasonChip: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  reasonChipSelected: {
    backgroundColor: 'rgba(0, 200, 83, 0.1)',
    borderColor: Colors.primary,
  },
  reasonText: {
    color: Colors.text,
    fontSize: 14,
  },
  reasonTextSelected: {
    color: Colors.primary,
    fontWeight: 'bold',
  },
  actionButtons: {
    width: '100%',
    gap: 15,
  },
  button: {
    width: '100%',
    height: 55,
  },
  pista: {
    marginTop: 10,
    fontSize: 12,
    color: Colors.textSecondary,
    textAlign: 'center',
  },
});
